import enclosure from '../shared/placeVisibility.js';
const array = value => Array.isArray(value) ? value : [];
const entityId = value => String(value?.idEntity || '').trim();

export const activeRoutineScene = state => state?.scenesById?.[state.settlementId];

export const routineBeingsNearPlayer = (state, currentLocation, placeId = '', radiusFeet = 160, additionalEnclosedIds = []) => {
  const scene = activeRoutineScene(state);
  const playerPoint = currentLocation?.location;
  if (!scene || !playerPoint) return [];
  const playerPlaceId = placeId || currentLocation.placeId || '';
  const enclosed = new Set([...array(scene.enclosedPlaceIds), ...additionalEnclosedIds]);
  const playerIndoors = enclosed.has(playerPlaceId);
  return Object.values(scene.beingsById || {}).flatMap(npc => {
    if (npc.being?.encounter?.discovered === false) return [];
    if (!npc.location) return [];
    if ((playerIndoors || npc.indoors || enclosed.has(npc.placeId)) && npc.placeId !== playerPlaceId) return [];
    const distance = Math.hypot(npc.location.x - playerPoint.x, npc.location.y - (playerPoint.y || 0), npc.location.z - playerPoint.z);
    if (distance > radiusFeet) return [];
    return [{ ...npc.being, location: npc.location, containingPlaceId: npc.placeId,
      currentActivity: npc.currentActivity, routineState: {
        status: npc.status, activities: npc.activities || [], legacyScheduledActivity: npc.legacyScheduledActivity, scheduledActivity: npc.scheduledActivity, destination: npc.destination,
        nextTransitionMinute: npc.nextTransitionMinute, nextCheckInMinute: npc.nextCheckInMinute,
        expectedArrivalMinute: npc.expectedArrivalMinute,
        interruptible: npc.interruptible, override: npc.override,
      }, proximityFeet: Math.round(distance), proximityType: playerIndoors ? 'same_interior' : 'nearby' }];
  }).sort((a, b) => a.proximityFeet - b.proximityFeet).slice(0, 10);
};

export const applyRoutineStateToPlaceContext = (context, state, currentLocation, placeId = '', profilesById = {}) => {
  const scene = activeRoutineScene(state);
  if (!context || !scene) return context;
  const tracked = new Set(Object.keys(scene.beingsById || {}));
  const existing = new Map([
    ...array(context.place?.savedInhabitants),
    ...array(context.subPlaces).flatMap(place => [...array(place.savedInhabitants), ...array(place.containedVisitors)]),
    ...array(context.beingsInLocation),
  ].map(being => [entityId(being), being]));
  const stripOccupants = place => place && ({ ...place,
    savedInhabitants: array(place.savedInhabitants).filter(being => !tracked.has(entityId(being))),
    containedVisitors: array(place.containedVisitors).filter(being => !tracked.has(entityId(being))),
    composedOfChildren: array(place.composedOfChildren).map(stripOccupants),
  });
  const liveBeings = routineBeingsNearPlayer(state, currentLocation, placeId, 160,
    [...array(context.enclosedPlaceIds), ...enclosure.enclosedPlaceIds([context.place, ...array(context.subPlaces)].filter(Boolean))]).map(being => ({
    ...existing.get(entityId(being)), ...profilesById[entityId(being)], ...being,
  }));
  return { ...context, place: stripOccupants(context.place), subPlaces: array(context.subPlaces).map(stripOccupants),
    beingsInLocation: [...array(context.beingsInLocation).filter(being => !tracked.has(entityId(being))), ...liveBeings] };
};

export const hydrateRoutinePlaceContext = async (context, state, currentLocation, placeId, profilesById, loadProfiles, { refresh = false } = {}) => {
  const visible = routineBeingsNearPlayer(state, currentLocation, placeId, 160,
    [...array(context?.enclosedPlaceIds), ...enclosure.enclosedPlaceIds([context?.place, ...array(context?.subPlaces)].filter(Boolean))]);
  const missing = visible.filter(being => refresh || !profilesById[entityId(being)]);
  const profiles = missing.length ? await loadProfiles(missing) : [];
  for (const profile of profiles.filter(Boolean)) profilesById[entityId(profile)] = profile;
  return applyRoutineStateToPlaceContext(context, state, currentLocation, placeId, profilesById);
};
